import { captureException } from '@sentry/react';
import { classifyHttpError, isCustomError, UnknownError } from 'errors';
import isEmpty from 'lodash/isEmpty';
import random from 'lodash/random';
import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { env, trackEvent } from 'utils';

import '../public/scss/adsense.scss';

import { bannerGroups, simpleData } from './constants';
import { NewsIdContext } from './News';
import { BannerOs } from './types';
import { request } from './utils/request';

interface BannerItem {
  id: number;
  desc?: string;
  imageUrl?: string;
  scheme?: string;
  title?: string;
  weight?: number;
  bgColor?: string;
}

interface BannerGroup {
  position?: number;
  banners?: BannerItem[];
  width?: number;
  height?: number;
  rotationPeriod?: number;
}

interface BannerProps {
  type: string;
  screenKey: string;
  os: BannerOs;
  adsenseSlot: string;
  adsenseKey: string;
  show?: boolean;
}

declare global {
  interface Window {
    adsbygoogle: unknown[];
  }
}

export const Banner = memo(function Banner({ type, screenKey, os, adsenseSlot, adsenseKey, show = true }: BannerProps) {
  const newsId = useContext(NewsIdContext);
  const [bannerGroup, setBannerGroup] = useState<BannerGroup>({});
  const { banners = [] } = bannerGroup;
  const [isPostAmplitude, setIsPostAmplitude] = useState(false);

  const { data: useAdsense } = useQuery(adsenseKey, () => {
    return request({
      method: 'GET',
      url: `${simpleData}/${adsenseKey}`,
    })
      .then((r) => {
        return r?.data?.data?.data;
      })
      .catch((error) => {
        let customError: unknown;

        const classifiedError = classifyHttpError(error);
        if (isCustomError(classifiedError)) {
          customError = classifiedError;
        } else {
          customError = new UnknownError(error);
        }

        captureException(customError);
      });
  });

  useEffect(() => {
    if (screenKey) {
      request({
        method: 'GET',
        url: bannerGroups,
        params: {
          screen: screenKey,
          os,
        },
      })
        .then((r) => {
          if (r.data.status === 'success') {
            const newBannerGroup = r?.data?.data?.bannerGroups?.[0];
            if (newBannerGroup) {
              setBannerGroup(newBannerGroup);
            }
          }
        })
        .catch((error) => {
          let customError: unknown;

          const classifiedError = classifyHttpError(error);
          if (isCustomError(classifiedError)) {
            customError = classifiedError;
          } else {
            customError = new UnknownError(error);
          }

          captureException(customError);
        });
    }
  }, [screenKey]);

  useEffect(() => {
    if (isEmpty(bannerGroup.banners) || !show || isPostAmplitude) {
      return;
    }

    if (!!banner?.id && !!newsId && !!screenKey) {
      trackEvent('배너노출', {
        page: '뉴스상세',
        bannerId: `${banner?.id || ''}`,
        pageDetail: '뉴스하단',
      });
      setIsPostAmplitude(true);
    }
  }, [bannerGroup, show, isPostAmplitude]);

  const isAdsense = useMemo(() => {
    const RATIO = 0.5;
    if (useAdsense !== 'on') {
      return false;
    }
    if (banners.length === 0) {
      return true;
    }

    return Math.random() >= RATIO;
  }, [banners, useAdsense]);

  const idx = random(0, banners.length - 1);
  const banner = banners[idx];
  const logBannerClick = useCallback(() => {
    if (!!banner?.id && !!newsId && !!screenKey) {
      trackEvent('배너클릭', {
        page: '뉴스상세',
        bannerId: `${banner?.id || ''}`,
        pageDetail: '뉴스하단',
      });
    }
  }, [banner?.id, newsId, screenKey]);

  if (isEmpty(banner)) {
    return null;
  }

  const { imageUrl, scheme, title, bgColor } = banner;

  const bgStyle = {
    ...(bgColor !== undefined && { backgroundColor: bgColor }),
  };

  return (
    <div className={`advertB ${type}`} style={bgStyle}>
      {isAdsense ? (
        <AdSense adsenseSlot={adsenseSlot} />
      ) : (
        <a href={scheme} onClick={logBannerClick}>
          <img src={imageUrl} alt={title} />
        </a>
      )}
    </div>
  );
});

export const AdSense = ({ adsenseSlot }: { adsenseSlot: string }) => {
  useEffect(() => {
    const adsbygoogle: unknown[] = window.adsbygoogle;
    try {
      adsbygoogle?.push({});
    } catch (error) {
      let customError: unknown;

      const classifiedError = classifyHttpError(error);
      if (isCustomError(classifiedError)) {
        customError = classifiedError;
      } else {
        customError = new UnknownError(error);
      }

      captureException(customError);
    }
  }, []);

  if (env.isDevelopment || env.isDevelopmentApi) {
    return (
      <img
        src="https://lh5.ggpht.com/mTRR6NXg718Xtg1pn_OlR3CgiJ0Z8-JcfQ5ewIltFkoUc5CKI002NEZ7YSUm_Ty0Dzd_X083=w895"
        alt="adsense example"
        style={{
          width: '100%',
          maxWidth: 694,
          height: 90,
        }}
      />
    );
  }

  return (
    <ins
      className="adsbygoogle news_adsense"
      style={{ display: 'inline-block' }}
      data-ad-client="ca-pub-2003041522762282"
      data-ad-slot={adsenseSlot}
    />
  );
};
